import { isAxiosError } from 'axios';
import { API_ROUTES } from '@/api/apiRoutes';
import { log } from '@/logger/logger';
import { parseFilenameFromContentDisposition } from '@/utils/urls';
import { safeAxios } from '@/services/safeAxios';

const logger = log.getLogger('workflowLineage');

/** Row count reported by the server, so the export analytics event doesn't have to parse the CSV. */
const ROW_COUNT_HEADER = 'x-wv-lineage-row-count';

export interface WorkflowAuditTrail {
  csv: string;
  /** From `Content-Disposition`; absent if the header was stripped in transit. */
  fileName?: string;
  /** Data rows in the CSV (excludes the header row); `undefined` if the server didn't report it. */
  rowCount?: number;
}

/**
 * Fetches the audit-trail CSV for a workflow. The report is built server-side from persisted run
 * records — the client no longer derives lineage from the live graph.
 */
export const getWorkflowAuditTrail = async (workflowId: string, signal?: AbortSignal): Promise<WorkflowAuditTrail> => {
  try {
    const response = await safeAxios.get<string>(API_ROUTES.recipes.auditTrail({ recipeId: workflowId }), { signal });
    const reportedRowCount = Number(response.headers[ROW_COUNT_HEADER]);
    return {
      csv: response.data,
      fileName: parseFilenameFromContentDisposition(response.headers['content-disposition']),
      rowCount: Number.isFinite(reportedRowCount) ? reportedRowCount : undefined,
    };
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    logger.error('workflow_audit_trail_download_failed', { status });
    throw error;
  }
};

export const getNodeAuditTrail = async (
  workflowId: string,
  nodeId: string,
  assetId?: string,
  signal?: AbortSignal,
): Promise<WorkflowAuditTrail> => {
  try {
    const response = await safeAxios.get<string>(API_ROUTES.recipes.nodeAuditTrail({ recipeId: workflowId, nodeId }), {
      signal,
      params: assetId != null ? { assetId } : undefined,
    });
    const reportedRowCount = Number(response.headers[ROW_COUNT_HEADER]);
    return {
      csv: response.data,
      fileName: parseFilenameFromContentDisposition(response.headers['content-disposition']),
      rowCount: Number.isFinite(reportedRowCount) ? reportedRowCount : undefined,
    };
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    logger.error('node_audit_trail_download_failed', { status });
    throw error;
  }
};
